import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  Tooltip,
  makeStyles,
  InputAdornment,
  IconButton,
  Chip,
  Fade
} from '@material-ui/core';
import TextField from '@mui/material/TextField';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@material-ui/icons';
import AuthApi from '../Services/Authapi';
import { ThemeContext } from '../../ThemeContext';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    marginBottom: '3%',
  },
  header: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(45deg, #1e1e1e 30%, #2d2d2d 90%)'
      : 'linear-gradient(45deg, #ffffff 30%, #f8f9fa 90%)',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    letterSpacing: '0.5px',
  },
  liveBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 20,
    backgroundColor: 'rgba(40, 167, 69, 0.1)',
    color: '#28a745',
    fontSize: '0.75rem',
    fontWeight: 600,
    border: '1px solid rgba(40, 167, 69, 0.2)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#28a745',
    boxShadow: '0 0 0 0 rgba(40, 167, 69, 0.7)',
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(40, 167, 69, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(40, 167, 69, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(40, 167, 69, 0)',
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchField: {
    width: 228,
    minWidth: 228,
  },
  refreshButton: {
    border: '1px solid #036dda',
    borderRadius: 12, // Rounded box
    padding: 8,
    marginLeft: theme.spacing(1),
    color: '#036dda',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(3, 109, 218, 0.1)',
      transform: 'rotate(180deg)',
      boxShadow: '0 0 10px rgba(3, 109, 218, 0.3)',
    },
  },
  filterSection: {
    padding: theme.spacing(1.5, 3),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#fafafa',
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
  },
  filterLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  chip: {
    fontWeight: 500,
    height: 28,
    fontSize: '0.8rem',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  activeChip: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  content: {
    padding: theme.spacing(3),
    minHeight: 250,
    maxHeight: 600,
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gap: theme.spacing(2),
  },
  userCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    border: '1px solid transparent',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
      borderColor: theme.palette.primary.light,
    },
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: theme.spacing(1.5),
  },
  avatar: {
    width: 64,
    height: 64,
    border: `3px solid ${theme.palette.background.paper}`,
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    fontSize: '1.2rem',
    fontWeight: 600,
  },
  userName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'center',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    marginBottom: 2,
  },
  userStatus: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontWeight: 500,
  },
  statusBadge: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    color: theme.palette.text.secondary,
    gap: theme.spacing(2),
  },
}));

const statusColorMap = {
  Working: '#28a745',
  'On Break': '#ffc107',
  Offline: '#dc3545',
  Absent: '#6c757d',
};

const EmployeePresenceMap = () => {
  const classes = useStyles();
  const { darkMode } = React.useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [presence, setPresence] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const loadData = async (force = false) => {
    setLoading(true);
    try {
      const res = await AuthApi.getEmployeePresenceMap(force);
      if (res && res.status) {
        setPresence(res.data || []);
      }
    } catch (e) {
      console.error("Error fetching presence map:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setSearchQuery('');
    setStatusFilter('All');
    loadData(true);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const filteredData = useMemo(() => {
    return presence.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = statusFilter === 'All' || user.status === statusFilter;
      return matchesSearch && matchesFilter;
    });
  }, [presence, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const counts = { All: presence.length, Working: 0, 'On Break': 0, Offline: 0, Absent: 0 };
    presence.forEach(user => {
      if (counts[user.status] !== undefined) {
        counts[user.status]++;
      }
    });
    return counts;
  }, [presence]);

  const handleFilterClick = (status) => {
    setStatusFilter(status);
  };

  return (
    <Card className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleSection}>
          <Typography className={classes.title}>Employee Presence</Typography>
          <div className={classes.liveBadge}>
            <div className={classes.pulseDot} />
            Live
          </div>
        </Box>
        <Box className={classes.controls}>
          <TextField
            label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            InputLabelProps={{
              shrink: searchQuery !== "",
              style: {
                color: darkMode ? '#ffffff' : '#000000',
                transform: searchQuery !== "" ? 'translate(14px, -6px) scale(0.75)' : 'translate(14px, 9px) scale(1)',
                transition: 'transform 0.2s ease-in-out',
                fontWeight: 400
              }
            }}
            InputProps={{
              style: {
                backgroundColor: darkMode ? '#2a2a2a' : 'lightgray',
                borderRadius: '25px',
                height: "40px",
                color: darkMode ? '#ffffff' : '#000000'
              },
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <ClearIcon onClick={handleClearSearch} style={{ cursor: 'pointer', color: darkMode ? '#ffffff' : 'inherit' }} />
                  )}
                  <SearchIcon style={{ cursor: 'pointer', color: darkMode ? '#ffffff' : 'inherit' }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} size="small" className={classes.refreshButton}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.filterSection}>
        <Typography className={classes.filterLabel}>
          <FilterIcon fontSize="small" /> Filters:
        </Typography>
        {['All', 'Working', 'On Break', 'Offline', 'Absent'].map((status) => (
          <Chip
            key={status}
            label={`${status} (${stats[status] || 0})`}
            clickable
            onClick={() => handleFilterClick(status)}
            className={`${classes.chip} ${statusFilter === status ? classes.activeChip : ''}`}
            variant={statusFilter === status ? 'default' : 'outlined'}
            style={{
              borderColor: statusFilter === status ? 'transparent' : (status === 'All' ? '#999' : statusColorMap[status]),
              color: statusFilter === status ? '#fff' : (status === 'All' ? 'inherit' : statusColorMap[status]),
              backgroundColor: statusFilter === status ? (status === 'All' ? '#036dda' : statusColorMap[status]) : 'transparent'
            }}
          />
        ))}
      </Box>

      <Box className={classes.content}>
        {loading && presence.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : filteredData.length > 0 ? (
          <div className={classes.grid}>
            {filteredData.map((user, index) => {
              const statusColor = statusColorMap[user.status] || user.color || '#888';

              return (
                <Fade in={true} timeout={300 + index * 50} key={user.user_id || index}>
                  <Tooltip
                    title={
                      <div style={{ textAlign: 'center', padding: 4 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#fff' }}>{user.name}</Typography>
                        <Typography variant="caption" display="block" style={{ color: '#eee' }}>{user.status}</Typography>
                        {user.since && <Typography variant="caption" style={{ opacity: 0.8, color: '#ddd' }}>Since: {user.since}</Typography>}
                      </div>
                    }
                    arrow
                    placement="top"
                  >
                    <div className={classes.userCard}>
                      <div className={classes.avatarContainer}>
                        <Avatar
                          className={classes.avatar}
                          src={user.photo}
                          alt={user.name}
                          style={{ borderColor: statusColor }}
                          onError={e => { e.target.onerror = null; e.target.src = undefined; }}
                        >
                          {getInitials(user.name)}
                        </Avatar>
                        <div
                          className={classes.statusBadge}
                          style={{ backgroundColor: statusColor }}
                        />
                      </div>
                      <Typography className={classes.userName}>{user.name}</Typography>
                      <Typography
                        className={classes.userStatus}
                        style={{ color: statusColor }}
                      >
                        {user.status}
                      </Typography>
                    </div>
                  </Tooltip>
                </Fade>
              );
            })}
          </div>
        ) : (
          <div className={classes.emptyState}>
            <SearchIcon style={{ fontSize: 48, opacity: 0.2 }} />
            <Typography>No employees found matching your criteria.</Typography>
          </div>
        )}
      </Box>
    </Card >
  );
};

export default EmployeePresenceMap;
